import { Box, TextFieldProps } from '@mui/material';
import { Button, Flex, IconButton, Input } from '@components';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { NomineeCustomer } from '@features/api/response/NomineeCustomer';
import { useForm } from 'react-hook-form';
import { nomineeCustomerFormSchema } from '@validationSchemas/nominee';
import { yupResolver } from '@hookform/resolvers/yup';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { useUpdateNomineeCustomerMutation } from '@features/api/nomineeCustomerSlice';
import { CategoryContainer } from './PersonalDetailsForm.styles';
import { InputProps } from '@components/Input/Input';

export interface PersonalDetailsFormProps {
  customer: NomineeCustomer;
  onSave: () => void;
}

function StyledInput({
  removeable = false,
  handleRemove,
  ...restProps
}: InputProps &
  TextFieldProps & { removeable?: boolean; handleRemove?: () => void }) {
  return (
    <Input
      {...restProps}
      InputProps={{
        sx: {
          flex: 1,
        },
        endAdornment: removeable ? (
          <IconButton
            onClick={handleRemove}
            size='small'
            iconBoxHeight={12}
            iconBoxWidth={12}
            icon={() => <DeleteOutlineIcon fontSize='small' color='error' />}
          />
        ) : null,
      }}
      sx={{
        flex: '0 49%',
      }}
    />
  );
}

export const PersonalDetailsForm = ({
  onSave,
  customer,
}: PersonalDetailsFormProps) => {
  const intl = useIntl();

  const [updateMutation] = useUpdateNomineeCustomerMutation();

  const {
    register,
    reset,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<Omit<NomineeCustomer, 'id'>>({
    resolver: yupResolver(nomineeCustomerFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      firstName: customer.firstName,
      middleName: customer.middleName,
      lastName: customer.lastName,
    },
  });

  const handleSave = async (
    data: Omit<NomineeCustomer, 'id' | 'categorizedData'>
  ) => {
    await updateMutation({
      id: customer.id,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      categorizedData: customer.categorizedData,
    }).unwrap();

    onSave();
    reset();
  };

  return (
    <Flex flex={1} flexDirection='column' justifyContent='space-between'>
      <Flex flex={1} flexDirection='column' gap={10}>
        <form onSubmit={handleSubmit(handleSave)}>
          <CategoryContainer>
            <Flex flexDirection='row' flexWrap='wrap' gap={6}>
              <StyledInput
                label={intl.formatMessage({ id: 'crm.firstName' })}
                {...registerInput(register, 'firstName')}
                {...getErrorProps(intl, errors.firstName)}
              />
              <StyledInput
                label={intl.formatMessage({ id: 'crm.middleName' })}
                {...registerInput(register, 'middleName')}
                {...getErrorProps(intl, errors.middleName)}
              />
              <StyledInput
                label={intl.formatMessage({ id: 'crm.lastName' })}
                {...registerInput(register, 'lastName')}
                {...getErrorProps(intl, errors.lastName)}
              />
            </Flex>
          </CategoryContainer>
        </form>
      </Flex>
      <Box mt={14}>
        <Button
          data-testid='save-button'
          variant='primary'
          type='submit'
          onClick={handleSubmit(handleSave)}
          disabled={!isValid}
        >
          <FormattedMessage id='misc.save' />
        </Button>
      </Box>
    </Flex>
  );
};
